// components/GeographicEfficiencyView.js
import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import {
  DirectionsCar,
  LocationOn,
  Timeline,
  Warning,
  Speed,
  Map,
  TrendingDown,
  Navigation,
  MyLocation,
  Close,
  Schedule,
  AccessTime
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  metricsContainer: {
    marginBottom: theme.spacing(2)
  },
  metricCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  metricIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1)
  },
  chartContainer: {
    height: 200,
    marginTop: theme.spacing(2)
  },
  crewList: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    }
  },
  crewItem: {
    marginBottom: theme.spacing(1),
    borderLeft: '4px solid transparent',
    '&.efficient': {
      borderLeftColor: theme.palette.success.main
    },
    '&.moderate': {
      borderLeftColor: theme.palette.warning.main
    },
    '&.inefficient': {
      borderLeftColor: theme.palette.error.main
    }
  },
  territoryChip: {
    fontSize: '0.75rem',
    height: 24
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  insightCard: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

// Crew Weekly Details Component
const CrewWeeklyDetails = ({ crew, weeklyRoutes, sites, selectedDate }) => {
  // Get all days for the week
  const weekStart = moment(selectedDate).startOf('week');
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  
  // Build daily data for this crew
  const dailyData = days.map(day => {
    const dayRoutes = weeklyRoutes.find(r => r.day === day);
    const crewRoute = dayRoutes?.routes?.find(r => r.crewId === crew.crewId);
    
    if (!crewRoute || !crewRoute.stops) {
      return {
        day,
        date: weekStart.clone().day(day),
        hasWork: false,
        stops: [],
        totalMiles: 0,
        totalHours: 0,
        efficiency: 0
      };
    }
    
    // Calculate day metrics
    const stopsWithSites = crewRoute.stops.map(stop => {
      const site = sites.find(s => s._id === stop.siteId);
      return {
        ...stop,
        siteName: site?.name || 'Unknown Site',
        address: site?.address || 'No address',
        estimatedHours: stop.estimatedHours || site?.estimatedHours || 1
      };
    });
    
    const totalHours = stopsWithSites.reduce((sum, stop) => sum + (stop.estimatedHours || 1), 0);
    const totalMiles = crewRoute.totalMilesDriven || 0;
    const efficiency = totalHours > 0 ? ((totalHours / (totalHours + (totalMiles / 30))) * 100) : 0; // Assuming 30 mph average
    
    return {
      day,
      date: weekStart.clone().day(day),
      hasWork: true,
      stops: stopsWithSites,
      totalMiles: Math.round(totalMiles),
      totalHours: totalHours.toFixed(1),
      efficiency: Math.round(efficiency),
      driveTime: Math.round(totalMiles / 30 * 60) // minutes
    };
  });
  
  const weekTotals = {
    totalStops: dailyData.reduce((sum, day) => sum + day.stops.length, 0),
    totalMiles: dailyData.reduce((sum, day) => sum + day.totalMiles, 0),
    totalHours: dailyData.reduce((sum, day) => sum + parseFloat(day.totalHours), 0),
    workingDays: dailyData.filter(day => day.hasWork).length
  };
  
  return (
    <Box>
      {/* Week Summary */}
      <Grid container spacing={3} style={{ marginBottom: '24px' }}>
        <Grid item xs={3}>
          <Paper style={{ padding: '16px', textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{weekTotals.totalStops}</Typography>
            <Typography variant="body2" color="textSecondary">Total Stops</Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper style={{ padding: '16px', textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{weekTotals.totalMiles}</Typography>
            <Typography variant="body2" color="textSecondary">Total Miles</Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper style={{ padding: '16px', textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{weekTotals.totalHours.toFixed(1)}</Typography>
            <Typography variant="body2" color="textSecondary">Total Hours</Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper style={{ padding: '16px', textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{weekTotals.workingDays}</Typography>
            <Typography variant="body2" color="textSecondary">Working Days</Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Daily Breakdown Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Day</strong></TableCell>
              <TableCell align="center"><strong>Stops</strong></TableCell>
              <TableCell align="center"><strong>Hours</strong></TableCell>
              <TableCell align="center"><strong>Miles</strong></TableCell>
              <TableCell align="center"><strong>Drive Time</strong></TableCell>
              <TableCell align="center"><strong>Efficiency</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dailyData.map((dayData) => (
              <TableRow 
                key={dayData.day}
                style={{ 
                  backgroundColor: !dayData.hasWork ? '#f5f5f5' : undefined,
                  opacity: !dayData.hasWork ? 0.6 : 1 
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{dayData.day}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {dayData.date.format('MMM DD')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {dayData.hasWork ? (
                    <Chip 
                      label={dayData.stops.length} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">No work</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {dayData.hasWork ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      <AccessTime fontSize="small" color="action" />
                      {dayData.totalHours}h
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell align="center">
                  {dayData.hasWork ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      <DirectionsCar fontSize="small" color="action" />
                      {dayData.totalMiles}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell align="center">
                  {dayData.hasWork ? `${dayData.driveTime}m` : '-'}
                </TableCell>
                <TableCell align="center">
                  {dayData.hasWork ? (
                    <Chip 
                      label={`${dayData.efficiency}%`}
                      size="small"
                      style={{
                        backgroundColor: dayData.efficiency > 75 ? '#4caf50' : 
                                        dayData.efficiency > 50 ? '#ff9800' : '#f44336',
                        color: 'white'
                      }}
                    />
                  ) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Detailed Stop List for Working Days */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Daily Stop Details
        </Typography>
        
        {dailyData.filter(day => day.hasWork).map((dayData) => (
          <Box key={dayData.day} mb={3}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {dayData.day} ({dayData.date.format('MMM DD')}) - {dayData.stops.length} stops
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Site</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell align="center"><strong>Hours</strong></TableCell>
                    <TableCell align="center"><strong>Time</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayData.stops.map((stop, index) => (
                    <TableRow key={stop.siteId}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {stop.siteName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {stop.address}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${stop.estimatedHours}h`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {stop.scheduledStartTime ? 
                            moment(stop.scheduledStartTime).format('h:mm A') : 
                            'TBD'
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const GeographicEfficiencyView = ({ crewMetrics, selectedDate, onCrewClick, weeklyRoutes, sites }) => {
  const classes = useStyles();
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedCrewDetails, setSelectedCrewDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Prepare data for visualizations
  const activeCrews = Object.values(crewMetrics).filter(crew => crew.workload.weeklyHours > 0);
  
  // Territory sprawl distribution
  const sprawlData = [
    { name: 'Compact', value: activeCrews.filter(c => c.geographic.territorySprawl === 'compact').length, color: '#4caf50' },
    { name: 'Moderate', value: activeCrews.filter(c => c.geographic.territorySprawl === 'moderate').length, color: '#ff9800' },
    { name: 'Excessive', value: activeCrews.filter(c => c.geographic.territorySprawl === 'excessive').length, color: '#f44336' }
  ];

  // Miles per stop ranking
  const milesPerStopData = activeCrews
    .sort((a, b) => b.geographic.avgMilesPerStop - a.geographic.avgMilesPerStop)
    .slice(0, 5)
    .map(crew => ({
      name: crew.crewName.split(' ')[0], // Shorten name for chart
      miles: crew.geographic.avgMilesPerStop,
      color: crew.crewColor
    }));

  // Calculate totals
  const totalMiles = activeCrews.reduce((sum, crew) => sum + crew.geographic.totalMiles, 0);
  const totalStops = activeCrews.reduce((sum, crew) => sum + crew.geographic.totalStops, 0);
  const avgMilesPerStop = totalStops > 0 ? (totalMiles / totalStops).toFixed(1) : 0;
  
  // Calculate cost estimates
  const fuelCost = (totalMiles / 15 * 4.50).toFixed(0); // Assuming 15 MPG and $4.50/gallon
  const mileageCost = (totalMiles * 0.655).toFixed(0); // IRS mileage rate

  // Get crew efficiency class
  const getEfficiencyClass = (crew) => {
    if (crew.geographic.territorySprawl === 'compact') return 'efficient';
    if (crew.geographic.territorySprawl === 'moderate') return 'moderate';
    return 'inefficient';
  };

  // Get territory health score
  const getTerritoryHealth = (crew) => {
    const avgMiles = crew.geographic.avgMilesPerStop;
    if (avgMiles <= 2) return { score: 100, label: 'Excellent', color: '#4caf50' };
    if (avgMiles <= 3) return { score: 80, label: 'Good', color: '#8bc34a' };
    if (avgMiles <= 4) return { score: 60, label: 'Fair', color: '#ff9800' };
    if (avgMiles <= 5) return { score: 40, label: 'Poor', color: '#ff5722' };
    return { score: 20, label: 'Critical', color: '#f44336' };
  };

  return (
    <Paper className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">
          Geographic Efficiency Analysis
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant={selectedView === 'overview' ? 'contained' : 'outlined'}
            onClick={() => setSelectedView('overview')}
          >
            Overview
          </Button>
          <Button
            size="small"
            variant={selectedView === 'details' ? 'contained' : 'outlined'}
            onClick={() => setSelectedView('details')}
          >
            Details
          </Button>
        </Box>
      </div>

      {/* Key Metrics */}
      <Grid container spacing={2} className={classes.metricsContainer}>
        <Grid item xs={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <DirectionsCar className={classes.metricIcon} color="primary" />
              <Typography variant="h4">{Math.round(totalMiles)}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total Miles Driven
              </Typography>
              <Tooltip title="Estimated fuel cost">
                <Typography variant="caption" style={{ color: '#f44336', fontWeight: 'bold' }}>
                  ~${fuelCost} fuel
                </Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <LocationOn className={classes.metricIcon} color="primary" />
              <Typography variant="h4">{totalStops}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total Stops
              </Typography>
              <Typography variant="caption" style={{ color: '#666' }}>
                ${(totalMiles / totalStops * 0.655).toFixed(2)}/stop
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Speed className={classes.metricIcon} color="primary" />
              <Typography variant="h4">{avgMilesPerStop}</Typography>
              <Typography variant="body2" color="textSecondary">
                Avg Miles/Stop
              </Typography>
              <Typography variant="caption" style={{ color: avgMilesPerStop > 3 ? '#f44336' : '#4caf50' }}>
                {avgMilesPerStop > 3 ? 'Above target' : 'On target'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Warning 
                className={classes.metricIcon} 
                style={{ 
                  color: sprawlData.find(d => d.name === 'Excessive')?.value > 0 ? '#f44336' : '#4caf50' 
                }}
              />
              <Typography variant="h4">
                {sprawlData.find(d => d.name === 'Excessive')?.value || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Inefficient Routes
              </Typography>
              <Typography variant="caption" style={{ color: '#f44336', fontWeight: 'bold' }}>
                ${mileageCost} total cost
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {selectedView === 'overview' ? (
        <>
          {/* Charts */}
          <Grid container spacing={2} style={{ height: 250, marginBottom: 16 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Territory Sprawl Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={sprawlData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {sprawlData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => value.toFixed(1)} />
                </PieChart>
              </ResponsiveContainer>
              <Box display="flex" justifyContent="center" gap={2} mt={1}>
                {sprawlData.map(item => (
                  <Box key={item.name} display="flex" alignItems="center" gap={0.5}>
                    <Box width={12} height={12} bgcolor={item.color} borderRadius="50%" />
                    <Typography variant="caption">{item.name} ({item.value})</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Highest Miles per Stop
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={milesPerStopData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => value.toFixed(1)} />
                  <RechartsTooltip formatter={(value) => value.toFixed(1)} />
                  <Bar dataKey="miles" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>

          {/* Insights */}
          {activeCrews.filter(c => c.geographic.territorySprawl === 'excessive').length > 0 && (
            <Card className={classes.insightCard}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Warning color="error" />
                <Typography variant="subtitle2">
                  Territory Optimization Needed
                </Typography>
              </Box>
              <Typography variant="body2">
                {activeCrews.filter(c => c.geographic.territorySprawl === 'excessive').length} crews 
                are driving excessive distances between stops. Consider reassigning distant properties 
                to closer crews or reorganizing territories.
              </Typography>
            </Card>
          )}
        </>
      ) : (
        /* Detailed View */
        <List className={classes.crewList}>
          {activeCrews
            .sort((a, b) => b.geographic.avgMilesPerStop - a.geographic.avgMilesPerStop)
            .map(crew => {
              const territoryHealth = getTerritoryHealth(crew);
              const efficiencyClass = getEfficiencyClass(crew);
              
              return (
                <Card 
                  key={crew.crewId} 
                  className={`${classes.crewItem} ${efficiencyClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCrewDetails(crew);
                    setDetailsOpen(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar 
                          style={{ 
                            backgroundColor: crew.crewColor,
                            width: 36,
                            height: 36
                          }}
                        >
                          {crew.crewName.charAt(0)}
                        </Avatar>
                        <div>
                          <Typography variant="subtitle2">
                            {crew.crewName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {crew.geographic.totalStops} stops • {Math.round(crew.geographic.totalMiles)} miles
                          </Typography>
                        </div>
                      </Box>
                      <Chip
                        label={crew.geographic.territorySprawl}
                        size="small"
                        className={classes.territoryChip}
                        style={{
                          backgroundColor: 
                            crew.geographic.territorySprawl === 'compact' ? '#e8f5e9' :
                            crew.geographic.territorySprawl === 'moderate' ? '#fff3e0' : '#ffebee',
                          color:
                            crew.geographic.territorySprawl === 'compact' ? '#2e7d32' :
                            crew.geographic.territorySprawl === 'moderate' ? '#e65100' : '#c62828'
                        }}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Navigation fontSize="small" color="action" />
                          <div>
                            <Typography variant="h6">
                              {crew.geographic.avgMilesPerStop.toFixed(1)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Avg mi/stop
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Timeline fontSize="small" color="action" />
                          <div>
                            <Typography variant="h6">
                              {crew.geographic.avgMilesBetweenStops}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Mi between
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <MyLocation fontSize="small" color="action" />
                          <div>
                            <Typography variant="h6">
                              {crew.geographic.longestDrive}m
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Max drive
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box mt={2}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption">
                          Territory Health
                        </Typography>
                        <Typography variant="caption" style={{ color: territoryHealth.color }}>
                          {territoryHealth.label} ({territoryHealth.score}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={territoryHealth.score}
                        className={classes.progressBar}
                        style={{
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: territoryHealth.color
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
        </List>
      )}

      {/* Detailed Crew Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCrewDetails && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar 
                    style={{ 
                      backgroundColor: selectedCrewDetails.crewColor,
                      width: 48,
                      height: 48
                    }}
                  >
                    {selectedCrewDetails.crewName.charAt(0)}
                  </Avatar>
                  <div>
                    <Typography variant="h6">
                      {selectedCrewDetails.crewName} - Weekly Details
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Week of {moment(selectedDate).startOf('week').format('MMM dd')} - {moment(selectedDate).endOf('week').format('MMM dd, yyyy')}
                    </Typography>
                  </div>
                </Box>
                <IconButton onClick={() => setDetailsOpen(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <CrewWeeklyDetails 
                crew={selectedCrewDetails}
                weeklyRoutes={weeklyRoutes}
                sites={sites}
                selectedDate={selectedDate}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default GeographicEfficiencyView;